import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Squad, createSquad, updateSquad, ScheduleItem, getSquads } from '../../api/squad.api';
import { getUsers } from '../../api/user.api';

interface SquadFormDialogProps {
  open: boolean;
  onClose: (refreshNeeded?: boolean) => void;
  squad?: Squad | null;
}

export const SquadFormDialog = ({ open, onClose, squad }: SquadFormDialogProps) => {
  const [formData, setFormData] = useState<any>({
    name: '',
    description: '',
    skillLevel: '',
    ageGroup: '',
    maxCapacity: 12,
    location: '',
    color: '#1a1a1a',
    trainingFee: '',
    coachId: '',
    term: '',
    schedule: [] as ScheduleItem[],
  });
  const [coaches, setCoaches] = useState<any[]>([]);
  const [existingSquads, setExistingSquads] = useState<Squad[]>([]);
  const [selectedCopySquad, setSelectedCopySquad] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingCoaches, setLoadingCoaches] = useState(true);
  const [loadingSquads, setLoadingSquads] = useState(true);

  useEffect(() => {
    if (open) {
      fetchCoaches();
      if (!squad) {
        fetchExistingSquads();
      }
      if (squad) {
        setFormData({
          name: squad.name || '',
          description: squad.description || '',
          skillLevel: squad.skillLevel || '',
          ageGroup: squad.ageGroup || '',
          maxCapacity: squad.maxCapacity || 12,
          location: squad.location || '',
          color: squad.color || '#1a1a1a',
          trainingFee: squad.trainingFee || '',
          coachId: squad.coachId || '',
          term: squad.term || '',
          schedule: squad.schedule || [],
        });
      } else {
        setFormData({
          name: '',
          description: '',
          skillLevel: '',
          ageGroup: '',
          maxCapacity: 12,
          location: '',
          color: '#1a1a1a',
          trainingFee: '',
          coachId: '',
          term: '',
          schedule: [],
        });
      }
    }
  }, [open, squad]);

  const fetchCoaches = async () => {
    try {
      setLoadingCoaches(true);
      const response = await getUsers({ role: 'COACH', limit: 100 });
      const headCoachResponse = await getUsers({ role: 'HEAD_COACH', limit: 100 });
      setCoaches([...response.data.data, ...headCoachResponse.data.data]);
    } catch (err) {
      console.error('Failed to load coaches:', err);
    } finally {
      setLoadingCoaches(false);
    }
  };

  const fetchExistingSquads = async () => {
    try {
      setLoadingSquads(true);
      const response = await getSquads({ limit: 100 });
      setExistingSquads(response.data || []);
    } catch (err) {
      console.error('Failed to load squads:', err);
      setExistingSquads([]);
    } finally {
      setLoadingSquads(false);
    }
  };

  const handleCopyFromSquad = (squadId: string) => {
    setSelectedCopySquad(squadId);
    const selectedSquad = existingSquads.find(s => s.id === squadId);
    if (selectedSquad) {
      setFormData({
        name: '',
        description: selectedSquad.description || '',
        skillLevel: selectedSquad.skillLevel || '',
        ageGroup: selectedSquad.ageGroup || '',
        maxCapacity: selectedSquad.maxCapacity || 12,
        location: selectedSquad.location || '',
        color: selectedSquad.color || '#1a1a1a',
        trainingFee: selectedSquad.trainingFee || '',
        coachId: selectedSquad.coachId || '',
        term: '',
        schedule: selectedSquad.schedule || [],
      });
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleAddSchedule = () => {
    setFormData({
      ...formData,
      schedule: [...formData.schedule, { dayOfWeek: 1, startTime: '16:00', endTime: '18:00' }],
    });
  };

  const handleRemoveSchedule = (index: number) => {
    const newSchedule = [...formData.schedule];
    newSchedule.splice(index, 1);
    setFormData({ ...formData, schedule: newSchedule });
  };

  const handleScheduleChange = (index: number, field: string, value: any) => {
    const newSchedule = [...formData.schedule];
    newSchedule[index] = { ...newSchedule[index], [field]: value };
    setFormData({ ...formData, schedule: newSchedule });
  };

  const getDayName = (day: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data: any = {
        name: formData.name,
        description: formData.description || undefined,
        skillLevel: formData.skillLevel || undefined,
        ageGroup: formData.ageGroup || undefined,
        maxCapacity: parseInt(formData.maxCapacity),
        location: formData.location || undefined,
        color: formData.color || '#1a1a1a',
        trainingFee: formData.trainingFee ? parseFloat(formData.trainingFee) : undefined,
        schedule: formData.schedule.length > 0 ? formData.schedule : undefined,
        term: formData.term || undefined,
        coachId: formData.coachId || undefined,
      };

      if (squad) {
        await updateSquad(squad.id, data);
      } else {
        await createSquad(data);
      }

      onClose(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            {squad ? 'Edit Squad' : 'Create New Squad'}
          </Typography>
          <IconButton onClick={() => onClose()}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {!squad && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Copy from Existing Squad (Optional)"
                  value={selectedCopySquad}
                  onChange={(e) => handleCopyFromSquad(e.target.value)}
                  disabled={loading || loadingSquads}
                  helperText="Select a squad to pre-fill details from"
                >
                  <MenuItem value="">Start from scratch</MenuItem>
                  {existingSquads.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name} {s.term ? `(${s.term})` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Squad Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Skill Level"
                value={formData.skillLevel}
                onChange={(e) => handleChange('skillLevel', e.target.value)}
                disabled={loading}
              >
                <MenuItem value="">Not Specified</MenuItem>
                <MenuItem value="beginner">Beginner</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
                <MenuItem value="elite">Elite</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Age Group"
                value={formData.ageGroup}
                onChange={(e) => handleChange('ageGroup', e.target.value)}
                disabled={loading}
              >
                <MenuItem value="">Not Specified</MenuItem>
                <MenuItem value="red">Red</MenuItem>
                <MenuItem value="orange">Orange</MenuItem>
                <MenuItem value="green">Green</MenuItem>
                <MenuItem value="yellow">Yellow</MenuItem>
                <MenuItem value="adult">Adult</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Max Capacity"
                value={formData.maxCapacity}
                onChange={(e) => handleChange('maxCapacity', e.target.value)}
                disabled={loading}
                inputProps={{ min: 1, max: 50 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Training Fee (£)"
                value={formData.trainingFee}
                onChange={(e) => handleChange('trainingFee', e.target.value)}
                disabled={loading}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Term"
                value={formData.term}
                onChange={(e) => handleChange('term', e.target.value)}
                disabled={loading}
              >
                <MenuItem value="">Not Specified</MenuItem>
                <MenuItem value="2025Q1">2025 Q1 (Jan-Mar)</MenuItem>
                <MenuItem value="2025Q2">2025 Q2 (Apr-Jun)</MenuItem>
                <MenuItem value="2025Q3">2025 Q3 (Jul-Sep)</MenuItem>
                <MenuItem value="2025Q4">2025 Q4 (Oct-Dec)</MenuItem>
                <MenuItem value="2026Q1">2026 Q1 (Jan-Mar)</MenuItem>
                <MenuItem value="2026Q2">2026 Q2 (Apr-Jun)</MenuItem>
                <MenuItem value="2026Q3">2026 Q3 (Jul-Sep)</MenuItem>
                <MenuItem value="2026Q4">2026 Q4 (Oct-Dec)</MenuItem>
                <MenuItem value="2027Q1">2027 Q1 (Jan-Mar)</MenuItem>
                <MenuItem value="2027Q2">2027 Q2 (Apr-Jun)</MenuItem>
                <MenuItem value="2027Q3">2027 Q3 (Jul-Sep)</MenuItem>
                <MenuItem value="2027Q4">2027 Q4 (Oct-Dec)</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Assigned Coach"
                value={formData.coachId}
                onChange={(e) => handleChange('coachId', e.target.value)}
                disabled={loading || loadingCoaches}
                helperText="Optional - leave blank if no coach assigned yet"
              >
                {loadingCoaches ? (
                  <MenuItem disabled>Loading coaches...</MenuItem>
                ) : coaches.length === 0 ? (
                  <MenuItem disabled>No coaches available</MenuItem>
                ) : (
                  coaches.map((coach) => (
                    <MenuItem key={coach.id} value={coach.id}>
                      {coach.firstName} {coach.lastName} ({coach.email})
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Training Schedule
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddSchedule}
                  disabled={loading}
                >
                  Add Session
                </Button>
              </Box>

              {formData.schedule.map((item: ScheduleItem, index: number) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        select
                        label="Day"
                        value={item.dayOfWeek}
                        onChange={(e) => handleScheduleChange(index, 'dayOfWeek', parseInt(e.target.value))}
                        size="small"
                      >
                        {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                          <MenuItem key={day} value={day}>
                            {getDayName(day)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={5} sm={3}>
                      <TextField
                        fullWidth
                        type="time"
                        label="Start"
                        value={item.startTime}
                        onChange={(e) => handleScheduleChange(index, 'startTime', e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={5} sm={3}>
                      <TextField
                        fullWidth
                        type="time"
                        label="End"
                        value={item.endTime}
                        onChange={(e) => handleScheduleChange(index, 'endTime', e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveSchedule(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}

              {formData.schedule.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No training sessions scheduled. Click "Add Session" to create a schedule.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => onClose()} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.name}
            startIcon={loading && <CircularProgress size={16} />}
          >
            {loading ? 'Saving...' : squad ? 'Update Squad' : 'Create Squad'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
